"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { FLOATING_PANEL_HOST_ID } from "@/features/notifications/lib/floatingPanelHost"

/**
 * A dialog scoped to the WORKSPACE COLUMN instead of the viewport.
 *
 * ## Why this exists
 *
 * A viewport dialog covers the whole shell, including the module rail and whatever panel is open
 * beside it. The rail is the only route to notifications, profile, logout and theming, and the
 * shell's chrome is between 68px and 332px wide depending on a per-user preference — so a dialog
 * centred on the viewport is not even centred on the work it belongs to. This one portals into
 * AppLayout's content column and positions itself inside it: the rail stays VISIBLE and undimmed,
 * and the dialog sits over the thing it is about.
 *
 * ## What it deliberately does NOT change
 *
 * Everything Radix gives us stays Radix's job — focus trap, Escape, focus restored to the trigger,
 * `aria-modal`, scroll lock. This component only supplies a different portal container and swaps
 * `fixed` for `absolute`. Re-implementing any of that by hand is how dialogs quietly lose their
 * keyboard behaviour, so don't.
 *
 * ⚠️ **The rail is visible, not interactive.** These stay modal, which means Radix blocks pointer
 * and focus interaction outside the content while one is open — including the rail. Making the rail
 * genuinely clickable requires `modal={false}`, which drops the focus trap with it. Visible-but-
 * inert is the trade chosen here; if the rail must be clickable mid-dialog, that is a product
 * decision that costs the focus trap and should be made explicitly.
 *
 * ## Fallback
 *
 * If the host column is not in the DOM — an auth page, a test harness, any screen outside
 * `AppLayout` — this falls back to a normal viewport dialog. That fallback is what makes it safe to
 * adopt one screen at a time, which is exactly how it is being rolled out.
 *
 * Drop-in for `@/components/ui/dialog`: same exports, same props. Swap the import path.
 */

/**
 * Resolved in an effect, never during render: on the first pass a consumer can run before AppLayout
 * has committed its column, and reading it in render would cache the miss for the component's life.
 * Same reason, same shape as `FloatingPostingRecoveryPanel`.
 */
function useWorkspaceHost(): HTMLElement | null {
  const [host, setHost] = React.useState<HTMLElement | null>(null)

  React.useEffect(() => {
    setHost(document.getElementById(FLOATING_PANEL_HOST_ID))
  }, [])

  return host
}

const WorkspaceHostContext = React.createContext<HTMLElement | null>(null)

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogClose = DialogPrimitive.Close

const DialogPortal = ({
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Portal>) => {
  const host = useWorkspaceHost()

  return (
    <WorkspaceHostContext.Provider value={host}>
      <DialogPrimitive.Portal container={host ?? undefined} {...props}>
        {children}
      </DialogPrimitive.Portal>
    </WorkspaceHostContext.Provider>
  )
}
DialogPortal.displayName = "WorkspaceDialogPortal"

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  const host = React.useContext(WorkspaceHostContext)

  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        // `absolute` covers the content column only, because that column is the portal's
        // containing block (AppLayout marks it `relative`, and warns not to drop that).
        host ? "absolute inset-0 z-50 bg-black/80" : "fixed inset-0 z-50 bg-black/80",
        className
      )}
      {...props}
    />
  )
})
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean
    /**
     * Classes for this dialog's scrim, merged over the default `bg-black/80` — e.g. `bg-black/50` for a
     * prototype's `rgb(0 0 0 / 0.5)`. Additive (PT-5 deliverable 1): a caller passing nothing is unchanged.
     */
    overlayClassName?: string
  }
>(({ className, children, hideCloseButton, overlayClassName, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <WorkspaceDialogInner ref={ref} className={className} hideCloseButton={hideCloseButton} {...props}>
      {children}
    </WorkspaceDialogInner>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const WorkspaceDialogInner = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean
  }
>(({ className, children, hideCloseButton, ...props }, ref) => {
  const host = React.useContext(WorkspaceHostContext)

  return (
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // No `animate-in` / `zoom-*` classes: tailwindcss-animate is NOT installed in this project,
        // so they compile to nothing. Carrying them over would only look like animation exists.
        host ? "absolute" : "fixed",
        // ⚠️ `sm:rounded-lg` is a RESPONSIVE variant and tailwind-merge does not dedupe it against
        // an unprefixed `rounded-*` in a caller's className — at >=640px the sm: rule wins. A caller
        // overriding the radius must pass the `sm:` prefix too. (Same trap as `ui/dialog`.)
        "left-[50%] top-[50%] z-50 grid max-h-[calc(100%-2rem)] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto border bg-background p-6 shadow-lg sm:rounded-lg",
        className
      )}
      {...props}
    >
      {children}
      {!hideCloseButton && (
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  )
})
WorkspaceDialogInner.displayName = "WorkspaceDialogInner"

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
